// Migrated
<template>
  <div class="flex flex-col border border-zinc-300 p-4 rounded-lg">
    <div class="mb-3 mt-1 md:mt-0">
      <slot name="title" />
    </div>

    <div class="flex flex-col md:flex-row">
      <ImagesThumbnailsCarousel
        class="w-full md:w-1/2"
        :images="infoCarouselImages"
        :initial-slide="initialSlide"
        only-render-visible-slides
        prevent-swiper-update
      />

      <div class="flex flex-col mt-4 md:mt-0 md:pl-6 relative w-full md:w-1/2 font-secondary">
        <LocationText v-bind="locationTextProps" />

        <div
          v-if="bedplace.description"
          class="mt-4 text-sm font-normal"
        >
          <span
            v-if="bedplace.allowHtmlDescription"
            v-html="bedplace.description"
          />
          <span v-else-if="bedplaceDescription">
            {{ bedplaceDescription }}
          </span>
        </div>

        <TripAdvisorRating
          :rating="bedplace.tripadvisor_rating"
          :review-count="bedplace.tripadvisor_reviews"
          class="md:mt-1"
          type="dots"
          show-review-count
          empty-dots
        />

        <div class="flex flex-col mt-4 h-full md:mt-8 justify-between">
          <div
            v-if="bedplace.highlighted_facilities?.length"
            class="block grid-cols text-sm font-normal font-secondary"
          >
            <FacilityIconLabel
              v-for="facility in bedplace.highlighted_facilities"
              :key="facility"
              class="text-nowrap mb-2"
              :facility="facility"
            />
          </div>
          <div class="flex justify-end mt-auto">
            <nuxt-link
              class="btn-blue bg-secondary font-primary hover:bg-blue-700 normal-case rounded-[1.25rem] px-4 py-1 font-semibold text-nowrap mt-4 max-w-min"
              :to="`${bedplaceTypeInfo.linkPathPrefix}/${bedplace.url_name}`"
              :data-i18n="bedplaceTypeInfo.linkTextTranslation"
            >
              {{ $t(bedplaceTypeInfo.linkTextTranslation) }}
            </nuxt-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'pinia'
import hotelImagesMixin, { supplierURLs } from '@packages/media/mixins/hotel-images.mixin'

const DESCRIPTION_MAX_LENGTH = 500

export default defineNuxtComponent({
  setup () {
    const { $sentryCaptureException } = useNuxtApp()

    return {
      $sentryCaptureException,
    }
  },

  mixins: [hotelImagesMixin],

  props: {
    bedplace: {
      type: Object,
      required: true,
    },

    initialSlide: {
      type: Number,
      default: 0,
    },

    bedplaceType: {
      type: String,
      default: 'hotel',
    },
  },

  data () {
    return {
      DESCRIPTION_MAX_LENGTH,
    }
  },

  computed: {
    ...mapState(useLocaleStore, [
      'localeURLs',
    ]),

    bedplaceTypeInfo () {
      switch (this.bedplaceType) {
        case 'hotel':
          return {
            linkTextTranslation: 'readMoreHotel',
            linkPathPrefix: this.localeURLs.hotels,
          }
        case 'ship':
          return {
            linkTextTranslation: 'readMoreShip',
            linkPathPrefix: this.localeURLs.shipinfo,
          }
      }
    },

    infoCarouselImages () {
      try {
        if (this.bedplace?.image && !this.bedplace?.meta.images && !this.bedplace?.images) {
          return [this.bedplace.image]
        }

        const images = (this.bedplace?.images || this.bedplace?.meta.images || []).slice(0, 11)

        return images
          .filter(Boolean)
          .map((i) => {
            if (Array.isArray(i?.image)) {
              return i.image
            }

            if (i?.image?.includes('https')) {
              return i.image
            }

            const type = i?.image_type || this.bedplace.meta.images_type || this.bedplace.supplier
            const baseUrl = supplierURLs[type]?.replace('[PATH]', i?.image || i)

            return {
              src: this.imageSize(baseUrl, 'max'),
              type,
              path: baseUrl,
              image: i.image,
            }
          })
      } catch (e) {
        this.$sentryCaptureException(e)
        return []
      }
    },

    locationTextProps () {
      return {
        destination: this.bedplace.zone_name || this.bedplace.address?.city,
        postcode: this.bedplace.address?.postal_code ?? this.bedplace.meta?.PostcodeZip,
        locality: this.bedplace.address?.city || this.bedplace.zone_name,
        country: this.bedplace.country,
      }
    },

    bedplaceDescription () {
      const description = this.bedplace.description.slice(0, DESCRIPTION_MAX_LENGTH)
      return description + (this.bedplace.description.length !== description.length ? '...' : '')
    },
  },
})
</script>
